import React from "react";
import { Label } from "semantic-ui-react";
import styled from "styled-components";
import Link from "next/link";
import variables from "@/common/globalVariables";
import TransformerContainer from "@/common/Layout/TransformerContainer";
import JobListingHeader from "./JobListingHeader";
import SEO from "@/components/SEO";
import sanitize from "@/lib/html";
import Title from "@/common/UI/Title";
import ApplyToJobButton from "@/common/UI/ApplyToJobButton";
import HtmlRenderer from "@/common/UI/HtmlRenderer";
import SocialMedia from "@/common/UI/Social/SocialMedia";
import StucturedJobListing from "./StructuredJobListing";
import { basePath, jobsSettings } from "@/root/config";
import Translator, { ListOfLanguages } from "@/components/hoc/Translator";
import JobCompensationBubbles from "../JobCompensationBubbles";
import appText from "@/lang/appText";
import { formatJobUrl } from "../JobList/JobListItem";

const StyledJobListing = styled.div`
  .Body {
    margin: 0 auto;
    padding: 28px 44px 48px;
    position: relative;
    border-bottom-right-radius: ${variables.roundedRadius};
    border-bottom-left-radius: ${variables.roundedRadius};
    color: ${variables.baseTextColor};
  }

  .JobToolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin: 0 0 28px;
  }

  .JobToolbarLabel {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${variables.accentColor2};
  }

  .JobToolbar .ListOfLanguages,
  .ListOfLanguages {
    position: static;
    z-index: 2;
  }

  .JobToolbar .ui.dropdown {
    min-width: 11em;
    font-size: 0.9em;
  }

  .JobSection {
    margin: 0 0 32px;
    padding: 0 0 28px;
    border-bottom: 1px solid ${variables.mutedColor2};
  }

  .JobSection.JobSectionMuted {
    border-bottom: none;
  }

  .JobSection .Title {
    margin: 0 0 16px;
  }

  .JobSection .Title h3 {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${variables.accentColor2};
    margin: 0;
  }

  .JobProse {
    width: 100%;
    font-size: 1.02rem;
    line-height: 1.75;
    color: #2a2a2a;
  }

  .JobProse p,
  .JobProse div p {
    margin: 0 0 1.05em;
    line-height: 1.75;
  }

  .JobProse p:last-child,
  .JobProse div > p:last-child {
    margin-bottom: 0;
  }

  .JobProse ul,
  .JobProse ol {
    padding: 0 0 0 28px;
    margin: 0 0 1.1em;
    color: ${variables.baseTextColor};
  }

  .JobProse li {
    list-style-image: url("${"../../../images/ExactStaffArrow.png"}");
    margin-bottom: 0.55em;
    line-height: 1.65;
    padding-left: 4px;
  }

  .JobProse strong,
  .JobProse b {
    font-weight: 650;
    color: ${variables.darkColor};
  }

  .JobSectionMuted {
    background: ${variables.mutedColor1};
    border: 1px solid ${variables.mutedColor2};
    border-bottom: 1px solid ${variables.mutedColor2};
    border-radius: 16px;
    padding: 22px 24px 24px;
  }

  .JobSectionMuted .Title h3 {
    color: ${variables.darkColor};
  }

  .JobFooter {
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding-top: 8px;
  }

  .JobShare {
    width: 100%;
  }

  .JobShare .ShareButtons {
    width: 100%;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: nowrap;
  }

  .JobShare .ShareButtons > * {
    flex: 1;
    min-width: 0;
    display: flex;
    justify-content: center;
  }

  .JobApply {
    width: 100%;
  }

  .JobApply .Button {
    width: 100%;
    float: none;
    border-radius: 10px;
    letter-spacing: 0.01em;
  }

  .Labels {
    margin-top: 4px;
    width: 100%;
  }

  .Labels .ui.label {
    background: ${variables.mutedColor1};
    border: 1px solid ${variables.mutedColor2};
    color: ${variables.darkColor};
    font-weight: 600;
    border-radius: 999px;
  }

  @media only screen and (max-width: 720px) {
    .Body {
      width: 100%;
      padding: 24px 20px 40px;
      border-bottom-right-radius: 0px;
      border-bottom-left-radius: 0px;
    }
  }
`;

const jobListing = (props) => {
  const listingUrl = `${basePath}${formatJobUrl(
    props.data.title,
    props.data.location,
    props.data.id
  )}`;

  return (
    <TransformerContainer $subtle data-test="job-listing">
      <StyledJobListing>
        <SEO
          description={
            sanitize(props.data.description, []).__html.substr(0, 400) +
            `...${appText.seo.description}`
          }
          NoIndex={props.data.status === "DELETED" ? true : false}
          title={
            props.data.title +
            ` ${appText.prepositions.at} ` +
            props.data.location +
            `- ${appText.seo.title}`
          }
          url={`${basePath}${formatJobUrl(
            props.data.title,
            props.data.location,
            props.data.id
          )}`}
          ogImage="/images/NowHiring.png"
          keywords={
            props.data.categories.map((cat) => cat.name).join(", ") +
            ", " +
            props.data.skills.map((skill) => skill.name).join(", ")
          }
        />
        <JobListingHeader
          perks={props.data.perks}
          title={props.data.title}
          location={props.data.location}
          minCompensation={props.data.minCompensation}
          maxCompensation={props.data.maxCompensation}
          type={props.data.type}
          data-test="title-section"
          hideFavoriteButton={props.preview}
          jobId={props.data.id}
          showPerks
          showType={jobsSettings.showJobType}
          showCompensation={jobsSettings.showPayRate}
          favoritesCount={props.data.favorites?.length || 0}
          showApply={!props.preview}
        />

        <div className="Body" data-test="main-content-section">
          <div className="JobToolbar">
            <span className="JobToolbarLabel">
              <Translator>Language</Translator>
            </span>
            <ListOfLanguages />
          </div>

          <section className="JobSection">
            <Title size={"m"}>
              <Translator>{appText.messages.job.jobDescription}</Translator>
            </Title>
            <div className="JobProse">
              <Translator>
                <HtmlRenderer html={props.data.description} />
              </Translator>
            </div>
          </section>

          <section className="JobSection">
            <Title size={"m"} data-test="compensation-section">
              <Translator>{appText.objects.compensation.singular}</Translator>
            </Title>
            <JobCompensationBubbles
              compensationType={props.data.compensationType}
              minCompensation={props.data.minCompensation}
              maxCompensation={props.data.maxCompensation}
            />
          </section>

          <section className="JobSection JobSectionMuted">
            <Title size={"m"} data-test="company-information-section">
              <Translator>
                {appText.messages.about} {props.data.company}
              </Translator>
            </Title>
            <div className="JobProse">
              <Translator>{props.data.aboutCompany}</Translator>
            </div>
          </section>

          <div className="JobFooter">
            {props.preview ? null : (
              <div className="JobShare">
                <SocialMedia url={listingUrl} />
              </div>
            )}

            {props.preview ? null : (
              <div className="JobApply">
                <ApplyToJobButton
                  jobId={props.data.id}
                  location={props.data.location}
                />
              </div>
            )}

            <div className="Labels">
              <Label.Group>
                {props.data.categories.map((category, index) => {
                  return (
                    <Link
                      href={`/jobs?category=${category.name}&location=${props.data.location}`}
                      key={"CategoryLabel" + index}
                      passHref
                      legacyBehavior
                    >
                      <Label as="a">
                        <Translator>{category.name}</Translator>
                      </Label>
                    </Link>
                  );
                })}
              </Label.Group>
            </div>
          </div>
        </div>
        <StucturedJobListing data={props.data} />
      </StyledJobListing>
    </TransformerContainer>
  );
};

export default jobListing;
